import { ReactNode, ComponentType, SVGProps } from 'react';

import { CubeIcon, UserCircleIcon } from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';

import { Card } from '@/components/Card';
import { useForecastQueryState } from '@/features/forecasts/hooks/useForecastQueryState';
import { useCdpPrimaryKeys } from '@/hooks/data/useCdpPrimaryKeys';
import { useTenantConfiguration } from '@/hooks/useTenantConfiguration';

type IconComponent = ComponentType<SVGProps<SVGSVGElement>>;

type MetricCardProps = {
  children?: ReactNode;
  Icon: IconComponent;
  label?: string;
  value?: ReactNode;
};

export function MetricCard({ children, Icon, label, value }: MetricCardProps) {
  return (
    <Card className="flex items-center space-x-3 p-3">
      <div className="bg-gray-100 rounded-lg p-1.5">
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0 space-y-1">
        {label && <p className="text-xs text-gray-500">{label}</p>}
        {value ? <p className="text-sm font-semibold text-gray-900">{value}</p> : null}
        {children}
      </div>
    </Card>
  );
}

export function OrderForecastSummary() {
  const { getTenantOrderSummaryData } = useTenantConfiguration();
  const { data } = useCdpPrimaryKeys({ include_static_features: true, limit: 1 });
  const { state } = useForecastQueryState();
  const { customerAddress, customerCity, customerName, productName, productDescription } =
    getTenantOrderSummaryData(data);
  const product = state.product || '';
  const shipTo = state.customer || '';
  const productLabel = productName || product;
  const customerLabel = customerName || shipTo;
  // Cross-app links to the new Data pages (was the legacy /catalog/*/details/* routes → 404).
  // Plain react-router Link with an explicit ?id (LinkPreserveSearch mishandles a string ?id).
  const productValue = product ? (
    <Link
      to={`/data/products?id=${encodeURIComponent(product)}`}
      className="text-[#274483] hover:text-[#1e3366] underline underline-offset-2"
    >
      {productLabel}
    </Link>
  ) : (
    productLabel
  );
  const customerValue = shipTo ? (
    <Link
      to={`/data/customers?id=${encodeURIComponent(shipTo)}`}
      className="text-[#274483] hover:text-[#1e3366] underline underline-offset-2"
    >
      {customerLabel}
    </Link>
  ) : (
    customerLabel
  );

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
      <MetricCard Icon={CubeIcon} label={'Product'} value={productValue}>
        {productDescription ? <p className="text-xs text-gray-500 mt-1">{productDescription}</p> : null}
      </MetricCard>
      <MetricCard Icon={UserCircleIcon} label={'Customer (Ship-To)'} value={customerValue}>
        {customerAddress || customerCity ? (
          <p className="text-xs text-gray-600 mt-1">{[customerAddress, customerCity].filter(Boolean).join(', ')}</p>
        ) : null}
      </MetricCard>
    </div>
  );
}
